import { CommunicationCategory, CommunicationMedium, TaskGroupIdentifier } from '@bariendo/lib/constants';
import { INBOX_CLINICAL_TASKS_FILTER } from '@bariendo/lib/inbox-tasks';
import { getReferenceString } from '@medplum/core';
import { Practitioner } from '@medplum/fhirtypes';
import { TypeFilter } from '@/components/NotificationTable/types';

/**
 * Builds query parameters for notifications with optional filtering.
 */
export function buildNotificationQueryParamsWithFilters(
  practitioner: Practitioner,
  status: string | string[],
  options: {
    typeFilter?: string;
    searchTerm?: string;
    sortParameter?: string;
    selectedProvider?: Practitioner;
  } = {}
) {
  const { typeFilter, searchTerm, sortParameter, selectedProvider } = options;

  const practitionerRef = getReferenceString(practitioner);
  const ownerRef = selectedProvider ? getReferenceString(selectedProvider) : practitionerRef;
  const statusValue = Array.isArray(status) ? status.join(',') : status;

  // CLINICAL tasks (orbit-provider-task) ride the inbox queries in the All
  // and Tasks views: individually assigned ones match this owner-scoped
  // query directly (owner = Practitioner per PR #1159); role-assigned ones
  // are CareTeam-owned (or unowned when the patient has no CareTeam) and
  // merged in by useNotificationTable's separate performer-scoped query.
  // Both scopes pass canCompleteClinicalTask for the viewer by construction.
  // Category-specific views (SMS, Calls, …) exclude them via their
  // focus:Communication.category filter.
  const params: Record<string, any> = {
    'group-identifier': `${TaskGroupIdentifier.RESOURCE_ASSIGNMENT},${TaskGroupIdentifier.PROVIDER_MENTION},${TaskGroupIdentifier.GENERAL_NOTIFICATION},${TaskGroupIdentifier.ORBIT_PROVIDER_TASK}`,
    owner: ownerRef,
    status: statusValue,
    _include: 'Task:focus',
  };

  if (typeFilter === TypeFilter.TASKS) {
    params._tasksFilter = INBOX_CLINICAL_TASKS_FILTER;
  } else if (typeFilter && typeFilter !== 'all' && typeFilter !== TypeFilter.FAX) {
    params['focus:Communication.category'] = typeFilter;
  }

  if (typeFilter === TypeFilter.FAX) {
    params._faxFilter = true;
  }

  const term = searchTerm?.trim();
  if (term) {
    params['_filter'] = `(code co "${term}" or patient.name co "${term}")`;
  }

  if (sortParameter) {
    params._sort = sortParameter;
  }

  return params;
}

export type NotificationQueryParams = ReturnType<typeof buildNotificationQueryParamsWithFilters>;

/**
 * Builds the combined GraphQL _filter string from notification query params.
 */
export function buildNotificationCombinedFilter(params: NotificationQueryParams): string | undefined {
  const filters: string[] = [];

  if (params._faxFilter) {
    filters.push(
      `focus:Communication.category eq "${CommunicationCategory.INBOUND}" and focus:Communication.medium eq "${CommunicationMedium.FAX}"`
    );
  } else if (params['focus:Communication.category']) {
    filters.push(`focus:Communication.category eq "${params['focus:Communication.category']}"`);
  }

  if (params._tasksFilter) {
    filters.push(params._tasksFilter);
  }

  if (params._filter) {
    filters.push(params._filter);
  }

  if (filters.length === 0) {
    return undefined;
  }

  return filters.join(' and ');
}

/**
 * Builds a REST Task search URL from notification query params (excludes GraphQL-only fields).
 */
export function buildNotificationTaskRestQuery(params: NotificationQueryParams, combinedFilter?: string): string {
  const queryParams = new URLSearchParams({
    'group-identifier': params['group-identifier'],
    owner: params.owner,
    status: params.status,
  });

  if (combinedFilter) {
    queryParams.set('_filter', combinedFilter);
  }

  return `Task?${queryParams.toString()}`;
}
